import type { IContentItem, IContentItemsContainer } from '@kontent-ai/delivery-sdk'
import type {
  IRefreshMessageData,
  IUpdateMessageData
} from '@kontent-ai/smart-link/types/lib/IFrameCommunicatorTypes'
import { defineStore } from 'pinia'
import _ from 'lodash'

interface PageState {
  page: IContentItem | null
  fetched: boolean
  linkedItems: IContentItemsContainer
  references: IContentItemsContainer
  referenceListVisible: boolean
}

const getPageReferences = async (
  linkedItems: IContentItemsContainer
): Promise<IContentItemsContainer> => {
  const referenceIds: string[] = []
  _.forOwn(linkedItems, (value, key) => {
    const elements = _.get(value, 'elements')
    if (elements) {
      _.forOwn(elements, (elementValue, _elementKey) => {
        if (_.get(elementValue, 'type') === 'rich_text') {
          const links = _.get(elementValue, 'links', [])
          _.forEach(links, (link: any) => {
            if (link.linkId) {
              referenceIds.push(link.linkId)
            }
          })
        }
      })
    }
  })
  if (referenceIds.length == 0) return {}
  const url = `/api/references?ids=${referenceIds.join(',')}`
  const response = await $fetch<any>(url)

  const data = response.data

  if (data) {
    const references = data.items.map((c: IContentItem) => _.assign(c, { isPageReference: true }))
    return _.keyBy(
      references.map((r: any) =>
        _.set(
          r,
          'elements.referenceOrderNumber.value',
          _.get(r, 'elements.referenceOrderNumber.value')!.toString()
        )
      ),
      'elements.referenceOrderNumber.value'
    )
  }
  return {}
}

export function createPageStore(id: string) {
  return defineStore(id, {
    state: (): PageState => ({
      page: null,
      fetched: false,
      linkedItems: {} as IContentItemsContainer,
      references: {} as IContentItemsContainer,
      referenceListVisible: true,
    }),
    actions: {
      async fetchData(itemId: string, latest = false, lang = 'en') {
        let url = `/api/page/${itemId}/${lang}`
        if (latest) url += '?latest=true'

        const response = await $fetch(url)
        if (response) {
          //@ts-ignore
          this.page = response.data.item as IContentItem
          //@ts-ignore
          this.linkedItems = response.data.linkedItems as IContentItemsContainer
          this.references = await getPageReferences(this.linkedItems)
          this.fetched = true
        }
      },
      updateItemData(data: IUpdateMessageData) {
        if (!this.page) return
        const targetLinkedItem = this.linkedItems[data.item.codename]

        if (targetLinkedItem) {
          for (let i = 0; i < data.elements.length; i++) {
            const element = data.elements[i]
            const type = element.type
            const newValue = element.data.value
            const propertyName = camelCasePropertyName(element.element.codename)
            if (type === 'text') {
              targetLinkedItem.elements[propertyName].value = newValue
            }
          }
        }
      },
      async refreshItemData(data: IRefreshMessageData) {
        const itemId = this.page?.system.id
        if (itemId) await this.fetchData(itemId, true)
      }
    },
    getters: {
      getLinkedItem:
        (state) =>
          <T>(codename: string) => {
            return state.linkedItems?.[codename] as T
          },
      pageTitle: (state) => {
        if (!state.page) return ''

        return state.page.elements.title.value
      },
      getPageReferences: (state) => () => {
        return state.references
      },
      getPageReferenceById: (state) => (itemId: string) => {
        if (state.fetched && state.references) {
          return Object.values(state.references).find(x => x.system.id === itemId);
        }
        return undefined;
      },
      getListAs:
        (state) =>
          <T>(codenames: string[]) => {
            if (codenames && codenames.length > 0) {
              return codenames.map((codename) => state.linkedItems?.[codename] as T)
            }

            return []
          }
    }
  })
}

export const usePageStore = createPageStore('page')
